import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import Topbar from '../components/Topbar';
import StatCard from '../components/StatCard';
import ODFlowPanel from '../components/ODFlowPanel';
import PredictionsChart from '../components/PredictionsChart';
import MobilityDesertsPanel from '../components/MobilityDesertsPanel';
import AnomaliesFeed from '../components/AnomaliesFeed';
import CrowdingPanel from '../components/CrowdingPanel';
import { IconPulse, IconDesert, IconAlert, IconUsers } from '../components/icons';

const compact = (n) =>
  new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 }).format(n);

export default function Overview() {
  const [s, setS] = useState({ loading: true, live: false, od: null, predictions: null, deserts: null, anomalies: null, crowding: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [health, od, predictions, deserts, anomalies, crowding] = await Promise.all([
        api.health(), api.odFlows(), api.predictions(), api.mobilityDeserts(), api.anomalies(), api.crowding(),
      ]);
      if (cancelled) return;
      setS({ loading: false, live: health.live, od: od.data, predictions: predictions.data, deserts: deserts.data, anomalies: anomalies.data, crowding: crowding.data });
    })();
    return () => { cancelled = true; };
  }, []);

  const kpis = useMemo(() => {
    const flows = s.od?.flows || [];
    const totalTrips = flows.reduce((a, f) => a + f.volume, 0);
    const deserts = s.deserts?.deserts || [];
    const highDeserts = deserts.filter((d) => /élev|haut/i.test(d.niveau || '')).length;
    const anomalies = s.anomalies?.anomalies || [];
    const highAnoms = anomalies.filter((a) => a.gravite === 'haute').length;
    const stops = s.crowding?.stops || [];
    const avgCrowd = stops.length ? Math.round((stops.reduce((a, x) => a + x.taux_occupation, 0) / stops.length) * 100) : 0;
    return { totalTrips, desertsCount: deserts.length, highDeserts, anomCount: anomalies.length, highAnoms, avgCrowd };
  }, [s]);

  return (
    <>
      <Topbar title="Vue d'ensemble" subtitle="Centre d'intelligence de la mobilité — région de Dakar" live={s.live} />
      <div className="content">
        <div className="kpi-row">
          <StatCard label="Déplacements / an" value={compact(kpis.totalTrips)} icon={IconPulse} trend={{ dir: 'up', text: 'données D4D' }} delay={0} />
          <StatCard label="Déserts de mobilité" value={String(kpis.desertsCount)} unit="zones" icon={IconDesert} trend={{ dir: kpis.highDeserts ? 'down' : 'flat', text: `${kpis.highDeserts} en niveau élevé` }} delay={70} />
          <StatCard label="Anomalies actives" value={String(kpis.anomCount)} icon={IconAlert} trend={{ dir: kpis.highAnoms ? 'down' : 'flat', text: `${kpis.highAnoms} en gravité haute` }} delay={140} />
          <StatCard label="Affluence moyenne" value={`${kpis.avgCrowd}`} unit="%" icon={IconUsers} trend={{ dir: kpis.avgCrowd >= 60 ? 'up' : 'flat', text: 'occupation réseau' }} delay={210} />
        </div>

        <div className="grid grid--main">
          <div className="col">
            <PredictionsChart data={s.predictions} delay={120} />
            <ODFlowPanel data={s.od} delay={180} />
          </div>
          <div className="col">
            <AnomaliesFeed data={s.anomalies} delay={150} />
            <MobilityDesertsPanel data={s.deserts} delay={210} />
            <CrowdingPanel data={s.crowding} delay={270} />
          </div>
        </div>
      </div>
    </>
  );
}
